import React, { useState, useEffect, useCallback, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import StepActions from './StepActions';

const ProblemStep = ({ data, onUpdate, onNext, onBack, isActive = true, stepIndex = 2, showBack = true }) => {
  const [selectedProblem, setSelectedProblem] = useState(data?.problemId || '');
  const [errors, setErrors] = useState({});
  
  const [problems, setProblems] = useState([]);
  const [loadingProblems, setLoadingProblems] = useState(true);
  const [problemsError, setProblemsError] = useState(null);
  
  const [showPopup, setShowPopup] = useState(false);
  const [selectedProblemForPopup, setSelectedProblemForPopup] = useState(null);
  const [problemPhotos, setProblemPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  
  const popupRef = useRef(null);

  useEffect(() => {
    fetchProblems();
  }, [data?.equipment?.id]);

  useEffect(() => {
    setSelectedProblem(data?.problemId || '');
  }, [data?.problemId, data?.equipment?.id]);

  const fetchProblems = async () => {
    try {
      setLoadingProblems(true);
      setProblemsError(null);

      const equipmentId = data?.equipment?.id;

      if (!equipmentId) {
        setProblems([]);
        setLoadingProblems(false);
        return;
      }

      const { data: problemsData, error: problemsError } = await supabase
        .from('problem')
        .select('id, name, description, photos, order')
        .eq('actif', true)
        .eq('equipment', equipmentId)
        .order('order', { ascending: true, nullsLast: true })
        .order('name', { ascending: true });

      if (problemsError) {
        console.error('Problems error:', problemsError);
        throw problemsError;
      }

      // Parser les photos pour chaque problème
      const problemsWithPhotos = (problemsData || []).map(problem => {
        let parsedPhotos = [];
        if (problem.photos) {
          try {
            if (typeof problem.photos === 'string') {
              parsedPhotos = JSON.parse(problem.photos);
            } else if (Array.isArray(problem.photos)) {
              parsedPhotos = problem.photos;
            }
          } catch (e) {
            console.error('Error parsing photos for problem:', problem.id, e);
          }
        }
        return {
          ...problem,
          photos: parsedPhotos
        };
      });

      setProblems(problemsWithPhotos || []);
    } catch (err) {
      console.error('Error fetching problems:', err);
      setProblemsError(err.message || 'Erreur lors du chargement des problèmes');
    } finally {
      setLoadingProblems(false);
    }
  };

  const fetchProblemPhotos = async (problemId) => {
    setLoadingPhotos(true);
    try {
      const { data, error } = await supabase
        .from('problem')
        .select('photos')
        .eq('id', problemId)
        .single();

      if (error) throw error;

      let parsedPhotos = [];
      if (data?.photos) {
        if (typeof data.photos === 'string') {
          parsedPhotos = JSON.parse(data.photos);
        } else if (Array.isArray(data.photos)) {
          parsedPhotos = data.photos;
        }
      }
      setProblemPhotos(parsedPhotos);
    } catch (error) {
      console.error('Error fetching problem photos:', error);
      setProblemPhotos([]);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const handleProblemSelect = (problem) => {
    setSelectedProblem(problem.id);
    if (errors?.problem) setErrors((prev) => ({ ...prev, problem: '' }));

    onUpdate({
      problemId: problem.id,
      problemName: problem.name,
    });
    onNext(true);
  };

  const handleSubmit = () => {
    const newErrors = {};

    if (!selectedProblem) {
      newErrors.problem = 'Veuillez sélectionner un type de problème';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    onUpdate({
      problemId: selectedProblem,
      problemName: problems.find(p => p.id === selectedProblem)?.name,
    });
    onNext();
  };

  const openPopup = useCallback(async (problem, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedProblemForPopup(problem);
    setShowPopup(true);
    // Charger les photos du problème
    await fetchProblemPhotos(problem.id);
  }, []);

  const closePopup = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowPopup(false);
    setSelectedProblemForPopup(null);
    setProblemPhotos([]);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        closePopup();
      }
    };

    if (showPopup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPopup, closePopup]);

  const ProblemDescriptionPopup = () => {
    if (!showPopup || !selectedProblemForPopup) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div 
          ref={popupRef}
          className="bg-card rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        >
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Info" size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {selectedProblemForPopup.name}
                </h3>
                <p className="text-xs text-muted-foreground">
                  Description détaillée
                </p>
              </div>
            </div>
            <button
              onClick={closePopup}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {/* Description */}
            {selectedProblemForPopup.description ? (
              <div className="prose prose-sm max-w-none mb-6">
                <h4 className="text-sm font-semibold text-foreground mb-2">Description :</h4>
                <p className="text-foreground leading-relaxed whitespace-pre-wrap">
                  {selectedProblemForPopup.description}
                </p>
              </div>
            ) : (
              <div className="text-center py-4 mb-6">
                <Icon name="FileText" size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">
                  Aucune description disponible pour ce problème.
                </p>
              </div>
            )}

            {/* Photos du problème */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Camera" size={16} className="text-primary" />
                Photos d'illustration
              </h4>
              
              {loadingPhotos ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : problemPhotos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {problemPhotos.map((photo, index) => {
                    const photoUrl = typeof photo === 'string' ? photo : photo.url;
                    return (
                      <div key={index} className="group relative">
                        <div className="aspect-square rounded-lg overflow-hidden border border-border bg-muted/20">
                          <img
                            src={photoUrl}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-200"
                            onError={(e) => {
                              e.target.src = '/placeholder-image.jpg';
                            }}
                            onClick={() => window.open(photoUrl, '_blank')}
                          />
                        </div>
                        {index === 0 && (
                          <div className="absolute top-2 left-2 bg-primary/80 text-white text-xs px-2 py-0.5 rounded">
                            Principale
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 bg-muted/20 rounded-lg">
                  <Icon name="Image" size={32} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Aucune photo disponible pour ce problème.
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-end p-6 border-t border-border bg-muted/20">
            <Button variant="default" size="sm" onClick={closePopup}>
              Fermer
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <ProblemDescriptionPopup />
      
      <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
        <div className="flex items-start gap-4  ">
          {/* <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Button variant="outline" size="sm" iconName="ArrowLeft" onClick={onBack}>
            </Button>
          </div> */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">Quel est le problème ?</h2>
            <p className="text-muted-foreground">
              Aidez-nous à comprendre la situation pour vous proposer l’intervention la plus adaptée.
            </p>
          </div>
        </div>
  {/* <StepActions isActive={isActive} className="flex justify-between pt-6 pb-6 sm:pt-8">
          <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={onBack}>
            Retour
          </Button>
        </StepActions> */}
               <div className="flex justify-between pt-6  border-t border-border">
</div>
        <div className="space-y-6">
          <div>
            {loadingProblems ? (
              <div className="flex items-center gap-2 p-4 border border-border rounded-lg bg-muted/20">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="text-sm text-muted-foreground">Chargement des problèmes...</span>
              </div>
            ) : problemsError ? (
              <div className="flex items-center gap-2 p-4 border border-error rounded-lg bg-error/5">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <span className="text-sm text-error">{problemsError}</span>
              </div>
            ) : problems.length === 0 ? (
              <div className="flex items-center gap-2 p-4 border border-border rounded-lg bg-muted/20">
                <Icon name="Info" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Aucun problème disponible pour cet équipement</span>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {problems.map((problem) => (
                    <div
                      key={problem.id}
                      className={`relative flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedProblem === problem.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/30 hover:bg-muted/20'
                      }`}
                      onClick={() => handleProblemSelect(problem)}
                    >
                      <div className="flex items-center h-5 flex-shrink-0">
                        <input
                          type="radio"
                          name="problem-type"
                          value={problem.id}
                          checked={selectedProblem === problem.id}
                          onChange={() => {}}
                          className="h-4 w-4 text-primary border-border focus:ring-primary/20"
                        />
                      </div>
                      <label className="flex-1 min-w-0 font-medium text-foreground cursor-pointer">
                        {problem.name}
                      </label>
                      <button
                        onClick={(e) => openPopup(problem, e)}
                        className="relative group flex-shrink-0 ml-auto p-1"
                        title="Voir la description et les photos"
                        type="button"
                        aria-label={`Détails : ${problem.name}`}
                      >
                        <Icon
                          name="Info"
                          size={16}
                          className="text-muted-foreground hover:text-primary transition-colors"
                        />
                        <span className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                          Voir les détails
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
                {errors?.problem && (
                  <p className="text-sm text-error mt-2">{errors?.problem}</p>
                )}
              </>
            )}
          </div>
        </div>

        <StepActions isActive={isActive} showBack={showBack} className="flex justify-between pt-8 mt-8 border-t border-border">
          <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={onBack}>
            Retour
          </Button>
          <Button
            variant="default"
            size="lg"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={handleSubmit}
            disabled={selectedProblem === ''}
          >
            Continuer
          </Button>
        </StepActions>
      </div>
    </div>
  );
};

export default ProblemStep;